import { everyInIterable } from '../iterable';
import { getElementContents, setElementTagName, simplifyAttributes } from './element';
import { isComment, isElement, isWhitespaceText } from './node';

export function isParagraphElement(node: Node | null): node is HTMLDivElement {
  return isElement(node) && node.tagName === 'DIV';
}

export function isBreakElement(node: Node | null): node is HTMLBRElement {
  return isElement(node) && node.tagName === 'BR';
}

export function maybeCollapseParagraphWithPreviousSibling(paragraph: HTMLDivElement): HTMLDivElement {
  if (!paragraph.parentNode || !isParagraphElement(paragraph.previousSibling)) {
    return paragraph;
  }

  const { previousSibling } = paragraph;
  previousSibling.appendChild(getElementContents(paragraph));
  paragraph.parentNode.removeChild(paragraph);

  return previousSibling;
}

export function isParagraphEmpty(paragraphLike: Element): boolean {
  return everyInIterable(
    paragraphLike.childNodes,
    childNode => isWhitespaceText(childNode) || isComment(childNode),
  );
}

export function isTraditionalParagraphElement(node: Node | null): node is HTMLParagraphElement {
  return isElement(node) && node.tagName === 'P';
}

export function createEmptyParagraph(document: Document): HTMLDivElement {
  const paragraph = document.createElement('div');
  const filler = document.createElement('br');
  paragraph.appendChild(filler);
  return paragraph;
}

export function convertTraditionalParagraphElement(
  originalElement: HTMLParagraphElement,
): HTMLParagraphElement | HTMLDivElement {
  const element = setElementTagName(originalElement, 'div');
  if (!isParagraphElement(element)) {
    return element;
  }

  const { ownerDocument: document, parentNode } = element;
  if (!document || !parentNode) {
    return element;
  }

  const { marginTop, marginBottom, marginLeft, marginRight } = element.style;

  // Insert a blank line, unless there is a small margin in the element's inline styles.
  if (!marginBottom || parseFloat(marginBottom) > 4) {
    const emptyLine = document.createElement('div');
    emptyLine.appendChild(document.createElement('br'));
    parentNode.insertBefore(emptyLine, element.nextSibling);
  }

  // Don't carry over top/bottom margins from paragraphs to divs, because they don't make sense anymore.
  if (marginTop) {
    element.style.marginTop = '';
  }
  if (marginBottom) {
    element.style.marginBottom = '';
  }

  // Clean up any leftover 0px horizontal margins and style attributes.
  if (marginLeft && marginLeft.match(/^[^1-9]+$/)) {
    element.style.marginLeft = '';
  }
  if (marginRight && marginRight.match(/^[^1-9]+$/)) {
    element.style.marginRight = '';
  }
  simplifyAttributes(element);

  return element;
}
